import { isObject } from 'imask/core/utils';
import {
  AvailableFilters,
  EmptyFilterBlock,
  FilteredItem,
  FilterKey,
  FiltersType,
  ParsedProductPreview,
  SelectedFilters,
} from 'src/types/marketplace';
import { getIntersection } from 'src/utils/getIntersection';
import { sortByCount } from 'src/utils/marketplace/sorting';

export const FILTERS_CONFIG: EmptyFilterBlock[] = [
  {
    filterKey: 'productType',
    titleKey: 'Тип',
  },
  {
    filterKey: 'tags',
    titleKey: 'Теги',
    search: true,
    collapsable: true,
  },
];

export const isFiltered = (filters: SelectedFilters) =>
  Object.values(filters)?.some((filters) => filters?.length);

export const getCheckboxesForFilter = (
  used: Record<string, number>,
  fullList: FilteredItem[],
) =>
  Object.keys(used)
    .map((id) => {
      const info = fullList.find((info) => info.id === id);

      return {
        id: id,
        name: info?.name || id,
        count: used[id],
      };
    })
    .sort(sortByCount);

export const getFiltersForCards = (
  cards: ParsedProductPreview[],
): AvailableFilters => {
  // map of used checkboxes with counter
  const used = {} as Record<FilterKey, Record<string, number>>;
  // map of unique checkboxes in current FilterBlock
  const all = {} as Record<FilterKey, FilteredItem[]>;

  FILTERS_CONFIG.forEach(({ filterKey }) => {
    used[filterKey] = {};
    all[filterKey] = [];
  });

  cards.forEach((card) => {
    FILTERS_CONFIG.forEach(({ filterKey }) => {
      const checkboxes = (
        card[filterKey] && Array.isArray(card[filterKey]) ? card[filterKey] : []
      ) as FilteredItem[];

      checkboxes.forEach((checkbox) => {
        used[filterKey][checkbox.id] = (used[filterKey][checkbox.id] || 0) + 1; // counting checkbox usage

        const savedTag = all[filterKey].find((t) => t.name === checkbox.name);

        if (!savedTag) {
          all[filterKey].push(checkbox); // save checkbox info
        }
      });
    });
  });

  return FILTERS_CONFIG.filter(
    ({ filterKey }) => Object.values(used[filterKey]).length,
  ).map((filter) => ({
    ...filter,
    options: getCheckboxesForFilter(
      used[filter.filterKey],
      all[filter.filterKey],
    ),
  }));
};

export const getSelectedFiltersNames = (
  filters: FiltersType,
  availableFilters: AvailableFilters,
): string[] => {
  if (!availableFilters.length) return [];

  let tagNames: string[] = [];

  availableFilters.forEach(({ filterKey, options }) => {
    const ids: string[] = filters[filterKey] || [];

    tagNames = [
      ...tagNames,
      ...options
        .filter((option) => ids.includes(option.id))
        .map((option) => option.name),
    ];
  });

  return tagNames;
};

export const getFilteredCards = (
  cards: ParsedProductPreview[],
  filters: SelectedFilters,
): ParsedProductPreview[] => {
  const filterKeys = Object.keys(filters);

  return cards.filter((card) =>
    filterKeys.some((filterKey) => {
      const filterable = card[filterKey as keyof ParsedProductPreview];

      return (
        Array.isArray(filterable) &&
        getIntersection(
          filterable.map((item) =>
            isObject(item) && 'id' in item ? item.id : item,
          ),
          filters[filterKey] || [],
        ).length
      );
    }),
  );
};

export const getSearchedCards = (
  cards: ParsedProductPreview[],
  search: string,
): ParsedProductPreview[] => {
  const searchString = search.trim().toLowerCase();

  return cards.filter(
    (c) =>
      c.displayName.toLowerCase().includes(searchString) ||
      c.previewText.toLowerCase().includes(searchString),
  );
};

export const getCardsToRender = (
  filteredCards: ParsedProductPreview[],
  searchedCards: ParsedProductPreview[],
  search: string,
): ParsedProductPreview[] => {
  return search.trim() ? searchedCards : filteredCards;
};

export const getFilterCount = (
  selectedFilters: string | string[] | undefined,
) => {
  if (!selectedFilters) {
    return 0;
  }

  return Array.isArray(selectedFilters) ? selectedFilters.length : 1;
};

export const isChecked = (
  id: string,
  selectedFilters: string | string[] | undefined,
) => {
  if (!selectedFilters) {
    return false;
  }

  return Array.isArray(selectedFilters)
    ? selectedFilters.includes(id)
    : selectedFilters === id;
};

export const getFilterTitleWithCount = (title: string, count: number) =>
  count ? `${title} (${count})` : title;

export const getFilterKeyMobile = (filterKey: FilterKey) =>
  `${filterKey}Mobile`;
